import React, { useState, useCallback } from 'react';
import { translations, patternStyles } from './translations';
import { SharinganCanvas } from './SharinganCanvas';

const App: React.FC = () => {
    const [lang, setLang] = useState('fr');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [animationTrigger, setAnimationTrigger] = useState<{ seed: number; userDescription: string; primaryPattern: number } | null>(null);
    const [msDescription, setMsDescription] = useState('');
    const [isDescriptionLoading, setIsDescriptionLoading] = useState(false);
    const [useAI, setUseAI] = useState(false);

    const t = translations[lang];

    const changeLanguage = (selectedLang: string) => {
        setLang(selectedLang);
        document.documentElement.lang = selectedLang;
        document.title = translations[selectedLang].pageTitle;
    };

    const generateLocalMsDescription = useCallback((userDescription: string, patternIndex: number) => {
        setIsDescriptionLoading(true);
        setMsDescription('');

        const currentTranslations = translations[lang];
        const analysis = currentTranslations.analysis;
        let finalDescription = "";

        const createSeededRandom = (seed: number) => { let s = seed; return () => { s = (1664525 * s + 1013904223) % 2**32; return s / 2**32; }; };
        const rand = createSeededRandom(patternIndex + userDescription.length);
        const randInt = (min: number, max: number) => Math.floor(rand() * (max - min + 1)) + min;

        // 1. Awakening phrase
        finalDescription += analysis.awakening_phrases[randInt(0, analysis.awakening_phrases.length - 1)] + "\n\n";

        // 2. Motivation based on keywords
        const lowerCaseDesc = userDescription.toLowerCase();
        let motivationFound = false;
        for (const key in analysis.keywords) {
            const keywords = analysis.keywords[key as keyof typeof analysis.keywords];
            if (keywords.some(keyword => lowerCaseDesc.includes(keyword))) {
                finalDescription += `${analysis.motivations[key as keyof typeof analysis.motivations]}\n`;
                motivationFound = true;
                break;
            }
        }
        if (!motivationFound) {
            finalDescription += `${analysis.motivations.generic}\n`;
        }

        // 3. Design and Powers
        finalDescription += `${analysis.design_connection}${patternStyles[lang][patternIndex]}.\n\n`;
        finalDescription += `${analysis.power_intro}\n`;

        const mainPower = analysis.powers[patternIndex % analysis.powers.length];
        finalDescription += `- **${mainPower.name}:** ${mainPower.desc}\n`;

        let secondPowerIndex;
        do {
            secondPowerIndex = randInt(0, analysis.powers.length - 1);
        } while (secondPowerIndex === (patternIndex % analysis.powers.length));
        const secondPower = analysis.powers[secondPowerIndex];
        finalDescription += `- **${secondPower.name}:** ${secondPower.desc}`;

        setTimeout(() => {
            setMsDescription(finalDescription);
            setIsDescriptionLoading(false);
        }, 500);

    }, [lang]);

    const generateAIDescription = useCallback(async (userDescription: string, patternIndex: number) => {
        setIsDescriptionLoading(true);
        setMsDescription('');

        const currentTranslations = translations[lang];
        const styleDescription = patternStyles[lang][patternIndex];
        const prompt = `${currentTranslations.promptPart1}"${userDescription}"${currentTranslations.promptPart2}${styleDescription}.${currentTranslations.promptPart3}`;

        // --- MODIFIED FOR OPENAI ---
        const apiKey = "VOTRE_CLÉ_API_OPENAI_ICI"; // IMPORTANT: Remplacez par votre clé
        const apiUrl = "https://api.openai.com/v1/chat/completions";

        const payload = {
            model: "gpt-3.5-turbo",
            messages: [{ role: "user", content: prompt }],
            max_tokens: 150,
        };

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${apiKey}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error(`API request failed with status ${response.status}`);

            const result = await response.json();

            if (result.choices && result.choices[0]?.message?.content) {
                setMsDescription(result.choices[0].message.content);
            } else {
                throw new Error("Invalid API response structure");
            }

        } catch (error) {
            console.error("Error generating MS description:", error);
            setMsDescription("Une erreur est survenue lors de la génération de la description.");
        } finally {
            setIsDescriptionLoading(false);
        }
    }, [lang]);

    const handleAnimationComplete = useCallback((userDescription: string, primaryPattern: number) => {
        if (useAI) {
            generateAIDescription(userDescription, primaryPattern);
        } else {
            generateLocalMsDescription(userDescription, primaryPattern);
        }
    }, [useAI, generateAIDescription, generateLocalMsDescription]);

    const handleGeneration = () => {
        setMsDescription('');
        if (name.length === 0 && description.length === 0) {
            alert(t.alert);
            return;
        }
        const combinedText = name + description;
        let seed = 0;
        for (let i = 0; i < combinedText.length; i++) {
            seed = (seed + combinedText.charCodeAt(i) * (i + 1)) % 2**32;
        }

        const createSeededRandom = (s: number) => { let seedVal = s; return () => { seedVal = (1664525 * seedVal + 1013904223) % 2**32; return seedVal / 2**32; }; };
        const rand = createSeededRandom(seed);
        const primaryPattern = Math.floor(rand() * 7);

        setAnimationTrigger({ seed, userDescription: description, primaryPattern });
    };

    return (
        <div className="bg-gray-900 text-white flex flex-col items-center min-h-screen p-4" style={{ fontFamily: "'Cinzel', serif" }}>
            <div className="w-full max-w-2xl text-center">
                <div className="mb-6">
                    <select id="languageSelector" onChange={(e) => changeLanguage(e.target.value)} value={lang} className="bg-gray-700 text-white p-3 rounded-lg border-2 border-gray-600 focus:border-red-500 focus:ring-red-500 transition duration-300 w-full max-w-xs mx-auto" style={{
                        WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                        backgroundImage: `url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23ffffff%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')`,
                        backgroundRepeat: 'no-repeat', backgroundPosition: 'right .7em top 50%', backgroundSize: '.65em auto'
                    }}>
                        <option value="fr">Français</option>
                        <option value="en">English</option>
                        <option value="es">Español</option>
                        {/* ... Autres langues */}
                    </select>
                </div>

                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-red-500 mb-4" style={{ textShadow: '0 0 10px rgba(239, 68, 68, 0.7), 0 0 20px rgba(239, 68, 68, 0.5)' }}>{t.mainTitle}</h1>
                <p className="text-gray-300 mb-8">{t.subtitle}</p>

                <div className="bg-gray-800 p-6 rounded-xl shadow-2xl shadow-red-900/20">
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-gray-700 text-white p-3 rounded-lg border-2 border-gray-600 focus:border-red-500 focus:ring-red-500 transition duration-300 mb-4" placeholder={t.namePlaceholder} />
                    <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="w-full h-24 bg-gray-700 text-white p-3 rounded-lg border-2 border-gray-600 focus:border-red-500 focus:ring-red-500 transition duration-300 mb-4" placeholder={t.descPlaceholder}></textarea>

                    <div className="flex items-center justify-center my-4">
                        <label htmlFor="ai-toggle" className="flex items-center cursor-pointer">
                            <span className="mr-3 text-gray-300">{t.useAIGeneration}</span>
                            <div className="relative">
                                <input type="checkbox" id="ai-toggle" className="sr-only" checked={useAI} onChange={() => setUseAI(!useAI)} />
                                <div className="block bg-gray-600 w-14 h-8 rounded-full"></div>
                                <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${useAI ? 'transform translate-x-6 bg-red-500' : ''}`}></div>
                            </div>
                        </label>
                    </div>

                    <button onClick={handleGeneration} disabled={animationTrigger !== null || isDescriptionLoading} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:shadow-red-500/50 transform hover:scale-105 transition-all duration-300 disabled:bg-gray-500 disabled:cursor-not-allowed">
                        {t.button}
                    </button>
                </div>
            </div>

            <div className="mt-8 p-2 bg-black rounded-full shadow-2xl shadow-red-500/40 border-4 border-red-800 w-11/12 max-w-sm md:max-w-md">
                <SharinganCanvas animationTrigger={animationTrigger} onAnimationComplete={handleAnimationComplete} />
            </div>

            { (isDescriptionLoading || msDescription) && (
                <div className="w-full max-w-2xl text-left mt-8 bg-gray-800 p-6 rounded-xl shadow-inner shadow-black/20">
                    <h2 className="text-2xl font-bold text-red-500 mb-4">{t.descriptionTitle}</h2>
                    {isDescriptionLoading ? (
                        <div className="flex justify-center items-center h-24">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
                        </div>
                    ) : (
                        <p className="text-gray-300 whitespace-pre-wrap">{msDescription}</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default App;
